import { UIEvent, useCallback, useMemo, useRef } from "react";

import { inRange } from "lodash";

import { TIMEOUT_DELAY } from "./useTrackScrollDepth.constants";

import { useTrack } from "@/hooks/useTrack/useTrack";
import { ScrollDepthReached } from "@/hooks/useTrack/useTrack.decl";
import { useAutoScrollState } from "../useAutoScrollState/useAutoScrollState";
import { useCall } from "../useCall/useCall";

const ranges = [
  [20, 25],
  [45, 50],
  [70, 75],
  // "100 + 1" since `inRange` is exclusive on the end value.
  [95, 100 + 1],
];

export function useTrackScrollDepth() {
  const { track } = useTrack();

  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenRanges = useRef<Set<number>>(new Set());
  const hasScrolled = useRef<boolean>(false);

  const { call } = useCall();
  const { autoScrollEnabled } = useAutoScrollState();

  const trackScrollDepth = useCallback(
    (depth: ScrollDepthReached["scroll_depth"]) => () => {
      track("scroll_depth_reached", {
        call_id: Number(call?.id),
        call_uuid: call?.uuid,
        scroll_depth: depth,
      });
    },
    [call?.id, call?.uuid, track]
  );
  const trackCallbacks = useMemo(
    () => [
      trackScrollDepth("25%"),
      trackScrollDepth("50%"),
      trackScrollDepth("75%"),
      trackScrollDepth("100%"),
    ],
    [trackScrollDepth]
  );

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!hasScrolled.current) {
      hasScrolled.current = true;
      track("transcript_scroll_started", {
        call_id: Number(call?.id),
        call_uuid: call?.uuid,
      });
    }

    // Do not do anything if autoscroll is enabled.
    if (autoScrollEnabled) {
      return;
    }

    // The user already scrolled, so we can clear the timeout.
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // We have to assert here.
    const event = e.target as HTMLDivElement;
    const { scrollTop, clientHeight, scrollHeight } = event;

    const percentageScrolled = Math.round(
      ((scrollTop + clientHeight) / scrollHeight) * 100
    );

    const callbackIndex = ranges.findIndex(([start, end]) =>
      inRange(percentageScrolled, start, end)
    );

    // Do not call any callback if you did not find one, or if you have already seen it.
    if (callbackIndex < 0 || seenRanges.current.has(callbackIndex)) {
      return;
    }

    timeoutId.current = setTimeout(() => {
      seenRanges.current.add(callbackIndex);
      trackCallbacks[callbackIndex]();
    }, TIMEOUT_DELAY);
  };

  return { onScroll };
}
